import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, Input, message, Radio, Space, Tabs, Tooltip, Typography } from 'antd';
import { CopyOutlined, LeftOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';

import {
    RichTextColorEditor,
    injectDefaultFontSizeIntoRichTextHtml,
    sanitizePodRichHtml
} from '../../../components/rich-text';
import { GET_COMMON_CONFIG_BY_KEY } from '../common-config/graphql/commonConfigQueries';
import { compressImageFile } from '../../../utils/compressImageFile';
import {
    POD_CAPTURE_CONFIG_KEY,
    SAVE_POD_CAPTURE_CONFIG_ALL_LANGS
} from './graphql/savePodCaptureConfigAllLangs';
import {
    GET_POD_CAPTURE_CONFIG_FOR_CLIENT,
    type PodCaptureCameraGroupClient,
    type PodCaptureCameraItemClient
} from './graphql/getPodCaptureConfigForClient';
import { UPLOAD_POD_CAPTURE_EXAMPLE_IMAGE } from './graphql/uploadPodCaptureExampleImage';

const { Text, Title } = Typography;

type PodStep = {
    stepNo: number;
    key: string;
    title: string;
    differentialTipRichText: string;
    requirementRichText: string;
    exampleImageUrl: string | null;
    exampleThumbUrl: string | null;
    /** 是否允许跳过本步；默认仅 key 为 unit（门牌小号）为 true */
    skippable?: boolean;
};

type LocaleContent = {
    persistentNotice: string;
    steps: PodStep[];
};

type PodConfig = {
    requiredPhotoCount: number;
    maxPhotoCount: number;
    contentJson: {
        version: number;
        locales: Record<string, LocaleContent>;
    };
};

type PodModeKey = 'cameraDoorNumber' | 'cameraDoorSubNumber';

type PodCaptureClientJson = {
    persistentNotice?: string;
    camera: Array<{
        cameraImgUrl: string | null;
        topTips: string;
        firstAttributedTips: string;
        secondAttributedTips: string;
        key: string;
        title: string;
        skippable?: boolean;
    }>;
};

const getPodClientGroupByName = (
    groups: PodCaptureCameraGroupClient[] | null | undefined,
    name: PodModeKey
): PodCaptureCameraGroupClient | null => {
    const list = groups || [];
    return list.find((g) => String(g?.name) === name) ?? null;
};

const getPodClientLocaleList = (
    group: PodCaptureCameraGroupClient | null | undefined,
    localeKey: string
): PodCaptureCameraItemClient[] => {
    if (!group) return [];
    if (localeKey === 'en') return group.en || [];
    if (localeKey === 'es') return group.es || [];
    return group.zh || [];
};

const BASE_LOCALE = 'zh';
const SUPPORTED_LOCALES: Array<{ key: string; label: string }> = [
    { key: 'zh', label: '中文' },
    { key: 'en', label: 'English' },
    { key: 'es', label: 'Español' }
];

const cloneSteps = (steps: PodStep[]): PodStep[] => steps.map((step) => ({ ...step }));

const examplePreviewStorageKey = (
    stepIndex: number,
    modeKey: PodModeKey = 'cameraDoorNumber'
) => `${modeKey}__${stepIndex}`;

const isAbsoluteHttpUrl = (s: string): boolean =>
    s.startsWith('http://') || s.startsWith('https://');

const resolveExamplePreviewUrl = (raw: string | null | undefined): string | null => {
    const v = (raw ?? '').trim();
    if (!v) return null;
    if (isAbsoluteHttpUrl(v)) return v;
    // 不再根据环境猜测 static 域名：objectKey（如 static/...）仅用于入库；
    // 页面预览统一以：
    // 1) 上传后缓存的 publicUrl（localExamplePreviewByKey）
    // 2) 或 getPodCaptureConfigForClient 的真实 cameraImgUrl（http(s)）
    // 为准。这里仅对 http(s) 直链放行，其他一律不解析。
    return null;
};

/** 与 getPodCaptureConfigForClient 一致：库内 objectKey 在页面上用服务端解析后的完整 URL 展示 */
const resolveExampleUrlForClientDisplay = (
    raw: string | null | undefined,
    _stepIndex: number,
    _modeKey: PodModeKey
): string | null => {
    const v = (raw ?? '').trim();
    if (!v) return null;
    const directPreviewUrl = resolveExamplePreviewUrl(v);
    if (directPreviewUrl) return directPreviewUrl;
    return v;
};

/** 折叠区 / 复制：cameraImgUrl 为客户端实际拿到的地址；保存仍用 buildClientJsonForSave（objectKey） */
const buildClientJsonForDisplay = (
    locale: LocaleContent,
    modeKey: PodModeKey,
    localExamplePreviewByKey: Record<string, string>
): PodCaptureClientJson => {
    const notice = locale.persistentNotice || '';
    return {
        camera: (locale.steps || []).map((step, idx) => {
            const storageKey = examplePreviewStorageKey(idx, modeKey);
            // 优先使用本地预览的 URL（上传后未保存时），然后是服务器解析的 URL，最后是原始值
            let cameraImgUrl = localExamplePreviewByKey[storageKey] || 
                              resolveExampleUrlForClientDisplay(
                                  step.exampleImageUrl ?? step.exampleThumbUrl,
                                  idx,
                                  modeKey
                              );
            return {
                cameraImgUrl: cameraImgUrl || null,
                topTips: notice,
                firstAttributedTips: injectDefaultFontSizeIntoRichTextHtml(
                    step.differentialTipRichText || '',
                    POD_RICH_TEXT_DEFAULT_FONT_PX
                ),
                secondAttributedTips: injectDefaultFontSizeIntoRichTextHtml(
                    step.requirementRichText || '',
                    POD_RICH_TEXT_DEFAULT_FONT_PX
                ),
                key: step.key || '',
                title: step.title || '',
                skippable: resolveStepSkippable(step)
            };
        })
    };
};

const fileToBase64Payload = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error ?? new Error('read failed'));
        reader.readAsDataURL(file);
    });

const toCnStepLabel = (stepNo: number): string => `第${stepNo}张`;

/** Default POD overlay copy: orange highlight + white key terms (matches mobile preview). */
const POD_TXT_ORANGE = '#fa8c16';
const POD_TXT_WHITE = '#ffffff';

/**
 * POD 叠字字号（与产品设计一致，且需与 RichTextColorEditor 中橘/白输入约定对齐）：
 * - 橘色常规文案：16px
 * - 白色强调文案：普通文字 22px；阿拉伯数字及 `{value}` 等占位 24px
 */
const POD_RICH_ORANGE_PX = 16;
const POD_RICH_WHITE_TEXT_PX = 22;
const POD_RICH_WHITE_NUMERIC_PX = 24;

/** 橘色常规文案（16px） */
const podOrangeText = (text: string): string =>
    `<span style="color:${POD_TXT_ORANGE};font-size:${POD_RICH_ORANGE_PX}px">${text}</span>`;
/** 白色强调：默认 22px；数字/占位请用 podNumericWhiteText() 或第二参 POD_RICH_WHITE_NUMERIC_PX */
const podWhiteText = (text: string, fontSizePx: number = POD_RICH_WHITE_TEXT_PX): string =>
    `<span style="color:${POD_TXT_WHITE}; font-weight:bold; font-size:${fontSizePx}px">${text}</span>`;
/** 白色强调中的数字、占位（24px），如差异化 `{value}` */
const podNumericWhiteText = (text: string): string => podWhiteText(text, POD_RICH_WHITE_NUMERIC_PX);
const podDiffParagraph = (content: string): string =>
    `<p style="font-size:${POD_RICH_ORANGE_PX}px;text-align:center">${content}</p>`;
const podRequirementParagraph = (content: string): string =>
    `<p style="font-size:${POD_RICH_ORANGE_PX}px">${content}</p>`;

/** 差异化提示中的 `{value}`：白字数字档 24px；各步共用（曾与「门牌大号」拆名，现实现一致故只保留一名） */
const POD_DIFF_TIP_PLACEHOLDER = podNumericWhiteText('{value}');

/** 与 RichTextColorEditor 默认展示一致；写入 HTML 供入库与 JSON 预览；与段落基础橙色字号一致 */
const POD_RICH_TEXT_DEFAULT_FONT_PX = POD_RICH_ORANGE_PX;

/** 为每步写入 skippable：默认仅门牌小号（key=unit）为 true */
const patchPodConfigSkippable = (config: PodConfig): PodConfig => ({
    ...config,
    contentJson: {
        ...config.contentJson,
        locales: Object.fromEntries(
            Object.entries(config.contentJson.locales).map(([k, lc]) => [
                k,
                {
                    ...lc,
                    steps: lc.steps.map((s) => ({
                        ...s,
                        skippable: typeof s.skippable === 'boolean' ? s.skippable : s.key === 'unit'
                    }))
                }
            ])
        ) as Record<string, LocaleContent>
    }
});

const resolveStepSkippable = (step: PodStep): boolean =>
    typeof step.skippable === 'boolean' ? step.skippable : step.key === 'unit';

const DEFAULT_POD_CONFIG_BY_MODE_UNPATCHED: Record<PodModeKey, PodConfig> = {
    cameraDoorNumber: {
        requiredPhotoCount: 3,
        maxPhotoCount: 6,
        contentJson: {
            version: 1,
            locales: {
                zh: {
                    persistentNotice: '请勿拍摄到人脸、指纹和证件',
                    steps: [
                        {
                            stepNo: 1,
                            key: 'label',
                            title: '第1张:面单照片(1/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('拍摄包裹 ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('清晰拍摄包裹面单，确保')}${podWhiteText('运单号、二维码或条形码')}${podOrangeText('中至少有一项可以清晰识别')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/842a77f73592.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 2,
                            key: 'houseNumber',
                            title: '第2张:门牌号(2/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('外门牌号 ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('清晰拍摄收件地址标识，')}${podWhiteText('如门牌号、地址牌、外立面等')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/386229382950.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 3,
                            key: 'placement',
                            title: '第3张:妥投位置(3/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('因')}${podWhiteText('未放置到安全位置')}${podOrangeText('导致丢件，可能面临罚款')}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('清晰拍摄包裹实际放置位置，需完整显示')}${podWhiteText('包裹及其周围环境')}${podOrangeText('，能够确认包裹已妥投')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/24ce5f100384.jpg',
                            exampleThumbUrl: null
                        }
                    ]
                },
                en: {
                    persistentNotice:
                        'Do not capture faces, fingerprints, or any identification documents in the photos.',
                    steps: [
                        {
                            stepNo: 1,
                            key: 'label',
                            title: 'Photo 1: Shipping Label (1/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Capture the package ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Clearly capture the shipping label, ensuring that at least one of the following is clearly readable: ')}${podWhiteText('the tracking number, QR code, or barcode.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/842a77f73592.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 2,
                            key: 'houseNumber',
                            title: 'Photo 2: House Number (2/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('House Number ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Clearly show the delivery address identifier')}${podWhiteText(' (e.g., house number, or building exterior).')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/386229382950.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 3,
                            key: 'placement',
                            title: 'Photo 3: Delivery Placement (3/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Package loss due to ')}${podWhiteText('unsafe delivery placement')}${podOrangeText(' may result in a penalty.')}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Clearly show the delivery placement, including ')}${podWhiteText('the full package and its surrounding environment, ')}${podOrangeText('to confirm successful delivery.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/24ce5f100384.jpg',
                            exampleThumbUrl: null
                        }
                    ]
                },
                es: {
                    persistentNotice:
                        'No capture rostros, huellas dactilares ni documentos de identificación en las fotos.',
                    steps: [
                        {
                            stepNo: 1,
                            key: 'label',
                            title: 'Foto 1: Etiqueta de envío (1/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Tome una foto del paquete ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Asegúrese de que la etiqueta de envío sea clara y que al menos uno sea legible: ')}${podWhiteText('número de seguimiento, código QR o código de barras.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/842a77f73592.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 2,
                            key: 'houseNumber',
                            title: 'Foto 2: Número de casa (2/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Número de casa ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Muestre claramente ')}${podWhiteText('el número ')}${podOrangeText('de casa')}${podWhiteText(' o el exterior')}${podOrangeText(' del edificio.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/386229382950.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 3,
                            key: 'placement',
                            title: 'Foto 3: Ubicación de entrega (3/3)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('La entrega ')}${podWhiteText('en lugar no seguro')}${podOrangeText(' puede resultar en multas.')}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Muestre claramente la colocación de entrega, incluyendo ')}${podWhiteText('el paquete completo y su entorno circundante, ')}${podOrangeText('para confirmar la entrega exitosa.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/24ce5f100384.jpg',
                            exampleThumbUrl: null
                        }
                    ]
                }
            }
        }
    },
    cameraDoorSubNumber: {
        requiredPhotoCount: 4,
        maxPhotoCount: 6,
        contentJson: {
            version: 1,
            locales: {
                zh: {
                    persistentNotice: '请勿拍摄到人脸、指纹和证件',
                    steps: [
                        {
                            stepNo: 1,
                            key: 'label',
                            title: '第1张:面单照片(1/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('拍摄包裹 ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('清晰拍摄包裹面单，确保')}${podWhiteText('运单号、二维码或条形码')}${podOrangeText('中至少有一项可以清晰识别')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/04426956466b.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 2,
                            key: 'houseNumber',
                            title: '第2张:门牌大号(2/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('外门牌号 ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('清晰拍摄收件地址大号标识，')}${podWhiteText('如楼号、办公楼、建筑外立面')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/107ded7aee73.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 3,
                            key: 'unit',
                            title: '第3张:门牌小号(3/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('门牌小号 ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('疑似公寓地址，清晰拍摄收件地址小号标识，')}${podWhiteText('如公寓单元号、房位或mailroom')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/471a03f12b57.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 4,
                            key: 'placement',
                            title: '第4张:妥投位置(4/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('因')}${podWhiteText('未放置到安全位置')}${podOrangeText('导致丢件，可能面临罚款')}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('清晰拍摄包裹实际放置位置，需完整显示')}${podWhiteText('包裹及其周围环境')}${podOrangeText('，能够确认包裹已妥投')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/deb5c2a168cf.jpg',
                            exampleThumbUrl: null
                        }
                    ]
                },
                en: {
                    persistentNotice:
                        'Do not capture faces, fingerprints, or any identification documents in the photos.',
                    steps: [
                        {
                            stepNo: 1,
                            key: 'label',
                            title: 'Photo 1: Shipping Label (1/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Capture the package ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Clearly capture the shipping label, ensuring that at least one of the following is clearly readable: ')}${podWhiteText('the tracking number, QR code, or barcode.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/04426956466b.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 2,
                            key: 'houseNumber',
                            title: 'Photo 2: Building Number (2/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Building Number ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Clearly show the main address identifier')}${podWhiteText(' (e.g., building number or building exterior).')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/107ded7aee73.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 3,
                            key: 'unit',
                            title: 'Photo 3: Unit Number (3/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Unit Number ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Apartment delivery: clearly capture the unit identifier')}${podWhiteText(' (e.g., apartment or suite number) or the mailroom.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/471a03f12b57.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 4,
                            key: 'placement',
                            title: 'Photo 4: Delivery Placement (4/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Package loss due to ')}${podWhiteText('unsafe delivery placement ')}${podOrangeText('may result a penalty.')}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Clearly show the delivery placement, including ')}${podWhiteText('the full package and its surrounding environment, ')}${podOrangeText('to confirm successful delivery.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/deb5c2a168cf.jpg',
                            exampleThumbUrl: null
                        }
                    ]
                },
                es: {
                    persistentNotice:
                        'No capture rostros, huellas dactilares ni documentos de identificación en las fotos.',
                    steps: [
                        {
                            stepNo: 1,
                            key: 'label',
                            title: 'Foto 1: Etiqueta de envío (1/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Tome una foto del paquete ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Asegúrese de que la etiqueta de envío sea clara y que al menos uno sea legible: ')}${podWhiteText('número de seguimiento, código QR o código de barras.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/04426956466b.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 2,
                            key: 'houseNumber',
                            title: 'Foto 2: Número de edificio (2/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Número del edificio ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Muestre claramente el identificador principal de la dirección, ')}${podWhiteText('el número de edificio o el exterior del edificio.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/107ded7aee73.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 3,
                            key: 'unit',
                            title: 'Foto 3: Número de unidad (3/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('Número del apartamento ')}${POD_DIFF_TIP_PLACEHOLDER}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Entrega en apartamento: capturar claramente ')}${podWhiteText('el número de unidad o la sala de paquetería.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/471a03f12b57.jpg',
                            exampleThumbUrl: null
                        },
                        {
                            stepNo: 4,
                            key: 'placement',
                            title: 'Foto 4: Ubicación de entrega (4/4)',
                            differentialTipRichText: podDiffParagraph(
                                `${podOrangeText('La entrega ')}${podWhiteText('en lugar no seguro ')}${podOrangeText('puede resultar en multas.')}`
                            ),
                            requirementRichText: podRequirementParagraph(
                                `${podOrangeText('Muestre claramente la colocación de entrega, incluyendo ')}${podWhiteText('el paquete completo y su entorno circundante, ')}${podOrangeText('para confirmar la entrega exitosa.')}`
                            ),
                            exampleImageUrl: 'static/pod-capture-example/2026/04/deb5c2a168cf.jpg',
                            exampleThumbUrl: null
                        }
                    ]
                }
            }
        }
    }
};

const DEFAULT_POD_CONFIG_BY_MODE: Record<PodModeKey, PodConfig> = {
    cameraDoorNumber: patchPodConfigSkippable(DEFAULT_POD_CONFIG_BY_MODE_UNPATCHED.cameraDoorNumber),
    cameraDoorSubNumber: patchPodConfigSkippable(DEFAULT_POD_CONFIG_BY_MODE_UNPATCHED.cameraDoorSubNumber)
};

const NORMAL_POD_STEP_KEYS = ['label', 'houseNumber', 'placement'] as const;

const getDefaultPodConfig = (modeKey: PodModeKey): PodConfig => DEFAULT_POD_CONFIG_BY_MODE[modeKey];

const getLocaleContent = (config: PodConfig, locale: string, modeKey: PodModeKey): LocaleContent => {
    const localeData = config.contentJson.locales[locale];
    if (localeData) return localeData;
    const baseLocaleData = config.contentJson.locales[BASE_LOCALE];
    const defaultForLocale = getDefaultPodConfig(modeKey).contentJson.locales[locale];
    return {
        persistentNotice:
            defaultForLocale?.persistentNotice ?? baseLocaleData?.persistentNotice ?? '',
        steps: cloneSteps(baseLocaleData?.steps ?? defaultForLocale?.steps ?? [])
    };
};

const clonePodConfig = (config: PodConfig): PodConfig =>
    JSON.parse(JSON.stringify(config)) as PodConfig;

const stepTitlePrefixByLocale = (localeKey: string, stepNo: number): string => {
    switch (localeKey) {
        case 'en':
            return `Photo ${stepNo}: `;
        case 'es':
            return `Foto ${stepNo}: `;
        default:
            return `${toCnStepLabel(stepNo)}: `;
    }
};

const renumberStepTitle = (title: string, stepNo: number, localeKey: string): string => {
    const nextPrefix = stepTitlePrefixByLocale(localeKey, stepNo);
    const currentTitle = (title || '').trim();
    if (!currentTitle) return nextPrefix.trim();
    const prefixPattern =
        localeKey === 'en'
            ? /^Photo\s+\d+\s*:\s*/i
            : localeKey === 'es'
              ? /^Foto\s+\d+\s*:\s*/i
              : /^第[零一二三四五六七八九十百千\d]+张\s*:\s*/;
    if (prefixPattern.test(currentTitle)) {
        return currentTitle.replace(prefixPattern, nextPrefix);
    }
    return currentTitle;
};

const ensureParagraphTextAlign = (html: string, align: 'center'): string => {
    const source = (html || '').trim();
    if (!source) return html;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div id="pod-rich-align-root">${source}</div>`, 'text/html');
        const root = doc.querySelector('#pod-rich-align-root');
        if (!root) return html;
        const paragraphs = root.querySelectorAll('p');
        if (paragraphs.length > 0) {
            paragraphs.forEach((paragraph) => {
                const currentStyle = paragraph.getAttribute('style') || '';
                if (!/text-align\s*:/i.test(currentStyle)) {
                    const nextStyle = currentStyle ? `${currentStyle};text-align:${align}` : `text-align:${align}`;
                    paragraph.setAttribute('style', nextStyle);
                }
            });
            return root.innerHTML;
        }
        return `<div style="text-align:${align}">${source}</div>`;
    } catch {
        return html;
    }
};

const normalizeDifferentialTipRichText = (html: string, stepKey: string): string =>
    stepKey === 'houseNumber' || stepKey === 'unit' ? ensureParagraphTextAlign(html, 'center') : html;

const isEffectivelyEmptyRichText = (html: string | undefined | null): boolean => {
    const s = (html ?? '').trim();
    if (!s) return true;
    try {
        const doc = new DOMParser().parseFromString(
            `<div id="__pod_empty_probe__">${s}</div>`,
            'text/html'
        );
        const root = doc.querySelector('#__pod_empty_probe__');
        return !root || !(root.textContent || '').trim();
    } catch {
        return false;
    }
};

/** 左侧手机预览：空库与端上一致，展示合并内置默认后的 HTML */
const resolvePreviewRichHtml = (
    stored: string | undefined,
    modeKey: PodModeKey,
    localeKey: string,
    stepKey: string,
    kind: 'differential' | 'requirement'
): string => {
    const trimmedStored = (stored ?? '').trim();
    const fromDefault = (): string => {
        const defLocale = getLocaleContent(getDefaultPodConfig(modeKey), localeKey, modeKey);
        const defStep = defLocale.steps.find((st) => st.key === stepKey);
        if (!defStep) return '';
        const raw =
            kind === 'differential' ? defStep.differentialTipRichText : defStep.requirementRichText;
        return (raw ?? '').trim();
    };
    const pick = isEffectivelyEmptyRichText(stored) ? fromDefault() : trimmedStored;
    const injected = injectDefaultFontSizeIntoRichTextHtml(pick, POD_RICH_TEXT_DEFAULT_FONT_PX);
    const normalized = kind === 'differential'
        ? normalizeDifferentialTipRichText(injected, stepKey)
        : injected;
    return sanitizePodRichHtml(normalized);
};

const normalizeLocaleStepsForMode = (
    steps: PodStep[],
    localeKey: string,
    modeKey: PodModeKey
): PodStep[] => {
    const currentSteps = cloneSteps(steps || []);
    if (modeKey === 'cameraDoorNumber') {
        const fallbackSteps = cloneSteps(getLocaleContent(getDefaultPodConfig(modeKey), localeKey, modeKey).steps)
            .filter((step) => NORMAL_POD_STEP_KEYS.includes(step.key as (typeof NORMAL_POD_STEP_KEYS)[number]))
            .map((step) => ({
                ...step,
                differentialTipRichText: normalizeDifferentialTipRichText(step.differentialTipRichText, step.key)
            }));
        const stepByKey = new Map(currentSteps.map((step) => [step.key, step]));
        const normalizedDefaultSteps = fallbackSteps.map((fallbackStep) => {
            const source = stepByKey.get(fallbackStep.key);
            const mergedStep = { ...fallbackStep, ...source };
            return {
                ...mergedStep,
                differentialTipRichText: normalizeDifferentialTipRichText(
                    mergedStep.differentialTipRichText ?? fallbackStep.differentialTipRichText,
                    mergedStep.key || fallbackStep.key
                )
            };
        });
        const extraSteps = currentSteps.filter(
            (step) =>
                step.key !== 'unit' &&
                !NORMAL_POD_STEP_KEYS.includes(step.key as (typeof NORMAL_POD_STEP_KEYS)[number])
        );
        return [...normalizedDefaultSteps, ...extraSteps].map((step, idx) => ({
            ...step,
            stepNo: idx + 1,
            title: renumberStepTitle(step.title, idx + 1, localeKey),
            differentialTipRichText: normalizeDifferentialTipRichText(step.differentialTipRichText, step.key)
        }));
    }
    return currentSteps.map((step, idx) => ({
        ...step,
        stepNo: idx + 1,
        title: renumberStepTitle(step.title, idx + 1, localeKey),
        differentialTipRichText: normalizeDifferentialTipRichText(step.differentialTipRichText, step.key)
    }));
};

const normalizeSingleModeConfig = (config: PodConfig, modeKey: PodModeKey): PodConfig => {
    const locales = Object.fromEntries(
        SUPPORTED_LOCALES.map(({ key }) => {
            const locale = getLocaleContent(config, key, modeKey);
            return [
                key,
                {
                    ...locale,
                    steps: normalizeLocaleStepsForMode(locale.steps, key, modeKey)
                }
            ];
        })
    ) as Record<string, LocaleContent>;
    const requiredPhotoCount =
        modeKey === 'cameraDoorNumber'
            ? NORMAL_POD_STEP_KEYS.length
            : Math.max(...Object.values(locales).map((locale) => locale.steps.length), 1);
    return {
        ...config,
        requiredPhotoCount,
        contentJson: {
            ...config.contentJson,
            locales
        }
    };
};

const buildDefaultConfigForMode = (modeKey: PodModeKey): PodConfig =>
    normalizeSingleModeConfig(clonePodConfig(getDefaultPodConfig(modeKey)), modeKey);

const normalizeConfigByMode = (
    configByMode: Partial<Record<PodModeKey, PodConfig>>
): Record<PodModeKey, PodConfig> => ({
    cameraDoorNumber: normalizeSingleModeConfig(
        configByMode.cameraDoorNumber ?? buildDefaultConfigForMode('cameraDoorNumber'),
        'cameraDoorNumber'
    ),
    cameraDoorSubNumber: normalizeSingleModeConfig(
        configByMode.cameraDoorSubNumber ?? buildDefaultConfigForMode('cameraDoorSubNumber'),
        'cameraDoorSubNumber'
    )
});

const persistentNoticeFromClientJson = (client: PodCaptureClientJson): string => {
    const fromRoot = (client.persistentNotice || '').trim();
    if (fromRoot) return fromRoot;
    const tips = (client.camera || []).map((c) => (c.topTips || '').trim()).filter(Boolean);
    return tips[0] || '';
};

const clientJsonToLocaleContent = (client: PodCaptureClientJson): LocaleContent => ({
    persistentNotice: persistentNoticeFromClientJson(client),
    steps: (client.camera || []).map((c, i) => ({
        stepNo: i + 1,
        key: c.key || '',
        title: c.title || '',
        differentialTipRichText: c.firstAttributedTips ?? '',
        requirementRichText: c.secondAttributedTips ?? '',
        exampleImageUrl: c.cameraImgUrl || null,
        exampleThumbUrl: null,
        skippable:
            typeof c.skippable === 'boolean' ? c.skippable : (c.key || '') === 'unit'
    }))
});

const parsePodCaptureConfigFromServer = (raw: string): Record<PodModeKey, PodConfig> | null => {
    try {
        const buildPodConfigFromCameraMap = (cameraMap: Record<string, unknown>): PodConfig | null => {
            const locales: Record<string, LocaleContent> = {};
            for (const [k, v] of Object.entries(cameraMap)) {
                if (Array.isArray(v)) {
                    locales[k] = clientJsonToLocaleContent({ camera: v as any[] } as PodCaptureClientJson);
                } else if (v && typeof v === 'object' && v !== null && 'camera' in v) {
                    locales[k] = clientJsonToLocaleContent(v as PodCaptureClientJson);
                }
            }
            if (Object.keys(locales).length === 0) return null;
            const maxSteps = Math.max(...Object.values(locales).map((l) => l.steps.length), 1);
            return { requiredPhotoCount: maxSteps, maxPhotoCount: 6, contentJson: { version: 1, locales } };
        };

        const parsed = JSON.parse(raw) as unknown;

        if (Array.isArray(parsed)) {
            const arrayGroups = parsed as Array<Record<string, unknown>>;
            const groupByName = new Map<string, Record<string, unknown>>(
                arrayGroups
                    .filter((item) => item && typeof item === 'object' && typeof item.name === 'string')
                    .map((item) => [String(item.name), item])
            );
            const normal = groupByName.get('cameraDoorNumber');
            const special = groupByName.get('cameraDoorSubNumber');
            if (normal && special) {
                const normalConfig = buildPodConfigFromCameraMap(normal);
                const specialConfig = buildPodConfigFromCameraMap(special);
                if (normalConfig && specialConfig) {
                    return { cameraDoorNumber: normalConfig, cameraDoorSubNumber: specialConfig };
                }
            }
            return null;
        }
    } catch {
        return null;
    }
    return null;
};

const PodCaptureConfigPage: React.FC = () => {
    const [activePodMode, setActivePodMode] = useState<PodModeKey>('cameraDoorNumber');
    const [configByMode, setConfigByMode] = useState<Record<PodModeKey, PodConfig>>(() =>
        normalizeConfigByMode({
            cameraDoorNumber: buildDefaultConfigForMode('cameraDoorNumber'),
            cameraDoorSubNumber: buildDefaultConfigForMode('cameraDoorSubNumber')
        })
    );
    const [previewLocale, setPreviewLocale] = useState<string>(BASE_LOCALE);
    const [mobilePreviewStepIndex, setMobilePreviewStepIndex] = useState(0);
    const stepCardRefs = useRef<Array<HTMLDivElement | null>>([]);
    const fourColumnLayoutRef = useRef<HTMLDivElement | null>(null);
    /** 后三列（语言编辑区）互相同步滚动，与第一列预览无关 */
    const localeColumnScrollRefs = useRef<Partial<Record<string, HTMLDivElement | null>>>({});
    const syncingLocaleScrollRef = useRef(false);
    const [leftEditorScrollMaxPx, setLeftEditorScrollMaxPx] = useState(360);
    const exampleFileInputRef = useRef<HTMLInputElement | null>(null);
    const pendingExampleRef = useRef<{ stepIndex: number; modeKey: PodModeKey } | null>(null);
    const localExamplePreviewByKeyRef = useRef<Record<string, string>>({});

    const [localExamplePreviewByKey, setLocalExamplePreviewByKey] = useState<Record<string, string>>({});

    const [uploadingExampleKey, setUploadingExampleKey] = useState<string | null>(null);

    const currentIndex = useMemo(() => 0, []);

    const { data: commonConfigData, loading: loadingPodConfig, refetch: refetchPodConfig } = useQuery(
        GET_COMMON_CONFIG_BY_KEY,
        {
            variables: { configKey: POD_CAPTURE_CONFIG_KEY },
            fetchPolicy: 'network-only'
        }
    );

    const showMissingServerKeyHint =
        !loadingPodConfig &&
        commonConfigData != null &&
        commonConfigData.getCommonConfigByKey === null;

    const [savePodCaptureConfigAllLangs, { loading: savingPodConfig }] = useMutation(SAVE_POD_CAPTURE_CONFIG_ALL_LANGS);
    const [uploadPodCaptureExampleImageMutation] = useMutation(UPLOAD_POD_CAPTURE_EXAMPLE_IMAGE);
    const [fetchPodClientConfig, { data: podClientData, loading: loadingPodClientConfig }] = useLazyQuery(
        GET_POD_CAPTURE_CONFIG_FOR_CLIENT,
        { fetchPolicy: 'network-only' }
    );
    const didAutoFetchClientPreviewRef = useRef(false);

    useEffect(() => {
        localExamplePreviewByKeyRef.current = localExamplePreviewByKey;
    }, [localExamplePreviewByKey]);

    useEffect(() => {
        const raw = commonConfigData?.getCommonConfigByKey?.configValue;
        if (!raw) return;
        const parsed = parsePodCaptureConfigFromServer(raw);
        if (parsed) setConfigByMode(normalizeConfigByMode(parsed));
    }, [commonConfigData?.getCommonConfigByKey?.configValue]);

    // 初始化时自动拉一次“客户端真实返回”，避免预览先显示本地拼装内容造成误导。
    // 注意：不挂到保存流程，也不阻塞页面渲染；失败则仍显示本地拼装 JSON。
    useEffect(() => {
        if (didAutoFetchClientPreviewRef.current) return;
        didAutoFetchClientPreviewRef.current = true;
        fetchPodClientConfig().catch(() => undefined);
    }, [fetchPodClientConfig]);

    useEffect(() => {
        return () => {
            Object.values(localExamplePreviewByKeyRef.current).forEach((u) => {
                if (u.startsWith('blob:')) URL.revokeObjectURL(u);
            });
        };
    }, []);

    useEffect(() => {
        // 只清理不再使用的 blob URL，避免不必要的重复加载
        const currentKeys = Object.keys(localExamplePreviewByKey);
        const prevKeys = Object.keys(localExamplePreviewByKeyRef.current);
        const removedKeys = prevKeys.filter(key => !currentKeys.includes(key));
        removedKeys.forEach(key => {
            const u = localExamplePreviewByKeyRef.current[key];
            if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
        });
    }, [localExamplePreviewByKey]);

    const currentConfig = configByMode[activePodMode];
    const localeData = currentConfig ? getLocaleContent(currentConfig, previewLocale, activePodMode) : undefined;
    const mobilePreviewStep = localeData?.steps[mobilePreviewStepIndex];

    const podClientGroupForActiveMode = useMemo(
        () => getPodClientGroupByName(podClientData?.getPodCaptureConfigForClient, activePodMode),
        [activePodMode, podClientData?.getPodCaptureConfigForClient]
    );

    const getResolvedExampleUrlFromClient = (stepIndex: number, localeKey: string): string | null => {
        const list = getPodClientLocaleList(podClientGroupForActiveMode, localeKey);
        const item = list[stepIndex];
        const raw = (item?.cameraImgUrl || '').trim();
        return raw && isAbsoluteHttpUrl(raw) ? raw : null;
    };

    const previewKey = examplePreviewStorageKey(mobilePreviewStepIndex, activePodMode);
    const examplePreviewSrc =
        localExamplePreviewByKey[previewKey] ??
        getResolvedExampleUrlFromClient(mobilePreviewStepIndex, previewLocale) ??
        resolveExamplePreviewUrl(mobilePreviewStep?.exampleImageUrl);

    const openExampleFilePicker = (stepIndex: number) => {
        pendingExampleRef.current = { stepIndex, modeKey: activePodMode };
        exampleFileInputRef.current?.click();
    };

    const onExampleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const file = input.files?.[0];
        const pending = pendingExampleRef.current;
        input.value = '';
        pendingExampleRef.current = null;
        if (!file || !pending) return;
        const { stepIndex: stepIdx, modeKey } = pending;
        if (!file.type.startsWith('image/')) {
            message.error('请选择图片文件');
            return;
        }
        const rawMaxMb = 25;
        if (file.size > rawMaxMb * 1024 * 1024) {
            message.error(`原图不能超过 ${rawMaxMb}MB`);
            return;
        }
        const storageKey = examplePreviewStorageKey(stepIdx, modeKey);
        setUploadingExampleKey(storageKey);
        try {
            const compressed = await compressImageFile(file, {
                maxWidth: 1600,
                maxHeight: 1600,
                quality: 0.82
            });
            const uploadMaxMb = 8;
            if (compressed.size > uploadMaxMb * 1024 * 1024) {
                message.error(`压缩后仍超过 ${uploadMaxMb}MB，请换一张较小的图`);
                return;
            }
            const fileBase64 = await fileToBase64Payload(compressed);
            const { data, errors } = await uploadPodCaptureExampleImageMutation({
                variables: {
                    input: {
                        fileBase64,
                        filename: compressed.name,
                        contentType: compressed.type || 'image/jpeg'
                    }
                }
            });
            if (errors?.length || !data?.uploadPodCaptureExampleImage?.objectKey) {
                throw new Error('upload failed');
            }
            const { objectKey, publicUrl } = data.uploadPodCaptureExampleImage;
            setLocalExamplePreviewByKey((prev) => {
                const old = prev[storageKey];
                if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
                return { ...prev, [storageKey]: publicUrl };
            });
            updateCurrentConfig((draft) => {
                const locales = { ...draft.contentJson.locales };
                SUPPORTED_LOCALES.forEach(({ key }) => {
                    const locale = getLocaleContent(draft, key, modeKey);
                    locales[key] = {
                        ...locale,
                        steps: locale.steps.map((s, sIdx) =>
                            sIdx === stepIdx ? { ...s, exampleImageUrl: objectKey, exampleThumbUrl: null } : s
                        )
                    };
                });
                return {
                    ...draft,
                    contentJson: {
                        ...draft.contentJson,
                        locales
                    }
                };
            });
            message.success('上传成功');
        } catch {
            message.error('上传失败，请重试');
        } finally {
            setUploadingExampleKey(null);
        }
    };

    /** 写入 common_config：cameraImgUrl 存 objectKey 或可直链的 http(s) */
    const buildClientJsonForSave = (locale: LocaleContent): PodCaptureClientJson => {
        const notice = locale.persistentNotice || '';
        return {
            camera: (locale.steps || []).map((step) => ({
                cameraImgUrl: step.exampleImageUrl || step.exampleThumbUrl || null,
                topTips: notice,
                firstAttributedTips: injectDefaultFontSizeIntoRichTextHtml(
                    step.differentialTipRichText || '',
                    POD_RICH_TEXT_DEFAULT_FONT_PX
                ),
                secondAttributedTips: injectDefaultFontSizeIntoRichTextHtml(
                    step.requirementRichText || '',
                    POD_RICH_TEXT_DEFAULT_FONT_PX
                ),
                key: step.key || '',
                title: step.title || '',
                skippable: resolveStepSkippable(step),
                dialogTitle: '',
                dialogAttributedTips: '',
                nodeName: '',
                sequenceLabel: ''
            }))
        };
    };

    const buildClientJsonPreviewForMode = (modeKey: PodModeKey) => {
        const cfg = configByMode[modeKey];
        const acc: Record<string, PodCaptureClientJson> = {};
        SUPPORTED_LOCALES.forEach(({ key }) => {
            const locale = getLocaleContent(cfg, key, modeKey);
            acc[key] = buildClientJsonForDisplay(
                locale,
                modeKey,
                localExamplePreviewByKey
            );
        });
        return {
            zh: acc.zh?.camera ?? [],
            en: acc.en?.camera ?? [],
            es: acc.es?.camera ?? []
        };
    };

    const clientJson = useMemo(() => {
        return {
            cameraDoorNumber: buildClientJsonPreviewForMode('cameraDoorNumber'),
            cameraDoorSubNumber: buildClientJsonPreviewForMode('cameraDoorSubNumber')
        };
    }, [configByMode, localExamplePreviewByKey]);

    const currentModeClientJson = useMemo(
        () => [
            {
                name: activePodMode,
                ...clientJson[activePodMode]
            }
        ],
        [activePodMode, clientJson]
    );
    const focusStepForPreview = (stepIndex: number, _anchorOffset = 0) => {
        setMobilePreviewStepIndex(stepIndex);
    };

    const onLocaleColumnScroll = (e: React.UIEvent<HTMLDivElement>, sourceLocaleKey: string) => {
        if (syncingLocaleScrollRef.current) return;
        const top = e.currentTarget.scrollTop;
        syncingLocaleScrollRef.current = true;
        try {
            SUPPORTED_LOCALES.forEach(({ key }) => {
                if (key === sourceLocaleKey) return;
                const el = localeColumnScrollRefs.current[key];
                if (el) el.scrollTop = top;
            });
        } finally {
            syncingLocaleScrollRef.current = false;
        }
    };

    const podEditorUiMounted =
        !(loadingPodConfig && commonConfigData === undefined) && Boolean(currentConfig && localeData);

    /** 各列滚动区 maxHeight 由视口与四列网格顶边距动态计算，并预留底部保存条 */
    useLayoutEffect(() => {
        if (!podEditorUiMounted) return;

        const VIEWPORT_BOTTOM_GAP_PX = 12;
        const SAVE_BAR_RESERVE_PX = 56;

        const measureLeftScrollMax = () => {
            const node = fourColumnLayoutRef.current;
            if (!node) return;
            const top = node.getBoundingClientRect().top;
            const vh = window.visualViewport?.height ?? window.innerHeight;
            const next = Math.floor(vh - top - VIEWPORT_BOTTOM_GAP_PX - SAVE_BAR_RESERVE_PX);
            const clamped = Math.max(200, next);
            setLeftEditorScrollMaxPx((prev) => (prev === clamped ? prev : clamped));
        };

        measureLeftScrollMax();
        const raf = requestAnimationFrame(measureLeftScrollMax);

        const ro = new ResizeObserver(measureLeftScrollMax);
        ro.observe(document.documentElement);

        window.addEventListener('resize', measureLeftScrollMax);
        window.visualViewport?.addEventListener('resize', measureLeftScrollMax);

        /**
         * 不在 capture 阶段监听每次 scroll：鼠标在左右列间隙滚动页面时，会高频触发测量 + setState，造成卡顿。
         * 左右列内部滚动一般不冒泡到 window，故原先只有中间区域「卡」。
         * 滚动结束后再防抖补一次，修正因整页滚动导致的可视高度变化。
         */
        let scrollSettleTimer: ReturnType<typeof setTimeout> | null = null;
        const onWindowScrollDebounced = () => {
            if (scrollSettleTimer != null) clearTimeout(scrollSettleTimer);
            scrollSettleTimer = setTimeout(() => {
                scrollSettleTimer = null;
                measureLeftScrollMax();
            }, 120);
        };
        window.addEventListener('scroll', onWindowScrollDebounced, { passive: true });

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener('resize', measureLeftScrollMax);
            window.visualViewport?.removeEventListener('resize', measureLeftScrollMax);
            window.removeEventListener('scroll', onWindowScrollDebounced);
            if (scrollSettleTimer != null) clearTimeout(scrollSettleTimer);
        };
    }, [podEditorUiMounted, showMissingServerKeyHint]);

    // 移除滚动同步，让左右两边滚动互不影响
    // useEffect(() => {
    //     const leftEl = leftEditorContainerRef.current;
    //     const rightEl = rightPreviewContainerRef.current;
    //     if (!leftEl || !rightEl) return;

    //     const onScroll = () => {
    //         rightEl.scrollTop = leftEl.scrollTop;
    //     };

    //     leftEl.addEventListener('scroll', onScroll, { passive: true });
    //     return () => leftEl.removeEventListener('scroll', onScroll as EventListener);
    // }, []);

    useEffect(() => {
        if (!localeData) return;
        if (mobilePreviewStepIndex > localeData.steps.length - 1) {
            setMobilePreviewStepIndex(Math.max(0, localeData.steps.length - 1));
        }
    }, [previewLocale, localeData, mobilePreviewStepIndex]);

    const updateCurrentConfig = (updater: (draft: PodConfig) => PodConfig) => {
        setConfigByMode((prev) => ({
            ...prev,
            [activePodMode]: updater(prev[activePodMode])
        }));
    };

    const updateLocaleData = (localeKey: string, updater: (locale: LocaleContent) => LocaleContent) => {
        updateCurrentConfig((draft) => {
            const locale = getLocaleContent(draft, localeKey, activePodMode);
            return {
                ...draft,
                contentJson: {
                    ...draft.contentJson,
                    locales: {
                        ...draft.contentJson.locales,
                        [localeKey]: updater(locale)
                    }
                }
            };
        });
    };

    const restoreStepRichTextField = useCallback(
        (
            localeKey: string,
            stepIndex: number,
            stepKey: string,
            field: 'differentialTipRichText' | 'requirementRichText'
        ) => {
            if (!(stepKey || '').trim()) {
                message.warning('请先填写本步 key，再恢复默认文案');
                return;
            }
            const def = getLocaleContent(getDefaultPodConfig(activePodMode), localeKey, activePodMode);
            const defStep = def.steps.find((s) => s.key === stepKey);
            if (!defStep) {
                message.warning('当前步骤无对应内置默认，无法恢复');
                return;
            }
            const raw =
                field === 'differentialTipRichText'
                    ? defStep.differentialTipRichText
                    : defStep.requirementRichText;
            const withInject = injectDefaultFontSizeIntoRichTextHtml(raw, POD_RICH_TEXT_DEFAULT_FONT_PX);
            const normalized =
                field === 'differentialTipRichText'
                    ? normalizeDifferentialTipRichText(withInject, stepKey)
                    : withInject;
            updateLocaleData(localeKey, (locale) => ({
                ...locale,
                steps: locale.steps.map((s, i) =>
                    i === stepIndex ? { ...s, [field]: normalized } : s
                )
            }));
            message.success('已恢复默认文案');
        },
        [activePodMode]
    );

    /** skippable 与 key 语义一致，三语同步 */
    const setStepSkippableAllLocales = (stepIndex: number, skippable: boolean) => {
        updateCurrentConfig((draft) => {
            const locales = { ...draft.contentJson.locales };
            SUPPORTED_LOCALES.forEach(({ key: localeKey }) => {
                const locale = getLocaleContent(draft, localeKey, activePodMode);
                const steps = locale.steps.map((s, sIdx) =>
                    sIdx === stepIndex ? { ...s, skippable } : s
                );
                locales[localeKey] = { ...locale, steps };
            });
            return {
                ...draft,
                contentJson: {
                    ...draft.contentJson,
                    locales
                }
            };
        });
    };

    const addStep = () => {
        updateCurrentConfig((draft) => {
            const locales = { ...draft.contentJson.locales };
            SUPPORTED_LOCALES.forEach((localeMeta) => {
                const locale = getLocaleContent(draft, localeMeta.key, activePodMode);
                const nextStepNo = locale.steps.length + 1;
                const nextStep: PodStep = {
                    stepNo: nextStepNo,
                    key: '',
                    title: `第${nextStepNo}张:标题`,
                    differentialTipRichText: '<p style="font-size:16px">差异化提示语</p>',
                    requirementRichText: '<p style="font-size:16px">拍摄要求</p>',
                    exampleImageUrl: null,
                    exampleThumbUrl: null,
                    skippable: false
                };
                locales[localeMeta.key] = { ...locale, steps: [...locale.steps, nextStep] };
            });
            return {
                ...draft,
                contentJson: {
                    ...draft.contentJson,
                    locales
                }
            };
        });
    };

    const removeStep = (stepIndex: number) => {
        updateCurrentConfig((draft) => {
            const locales = { ...draft.contentJson.locales };
            SUPPORTED_LOCALES.forEach((localeMeta) => {
                const locale = getLocaleContent(draft, localeMeta.key, activePodMode);
                const steps = locale.steps
                    .filter((_, idx) => idx !== stepIndex)
                    .map((step, idx) => ({ ...step, stepNo: idx + 1 }));
                locales[localeMeta.key] = { ...locale, steps };
            });
            return {
                ...draft,
                contentJson: {
                    ...draft.contentJson,
                    locales
                }
            };
        });
    };

    const handleSave = async () => {
        try {
            await savePodCaptureConfigAllLangs({
                variables: {
                    input: {
                        cameraDoorNumber: {
                            zh: buildClientJsonForSave(getLocaleContent(configByMode.cameraDoorNumber, 'zh', 'cameraDoorNumber')),
                            en: buildClientJsonForSave(getLocaleContent(configByMode.cameraDoorNumber, 'en', 'cameraDoorNumber')),
                            es: buildClientJsonForSave(getLocaleContent(configByMode.cameraDoorNumber, 'es', 'cameraDoorNumber'))
                        },
                        cameraDoorSubNumber: {
                            zh: buildClientJsonForSave(getLocaleContent(configByMode.cameraDoorSubNumber, 'zh', 'cameraDoorSubNumber')),
                            en: buildClientJsonForSave(getLocaleContent(configByMode.cameraDoorSubNumber, 'en', 'cameraDoorSubNumber')),
                            es: buildClientJsonForSave(getLocaleContent(configByMode.cameraDoorSubNumber, 'es', 'cameraDoorSubNumber'))
                        }
                    }
                }
            });
            await refetchPodConfig();
            message.success('保存成功');
            await fetchPodClientConfig().catch(() => undefined);
        } catch {
            message.error('保存失败，请稍后重试');
        }
    };

    const handleCopySavedJson = async () => {
        try {
            const preview = podClientData?.getPodCaptureConfigForClient ?? currentModeClientJson ?? {};
            await navigator.clipboard.writeText(JSON.stringify(preview, null, 2));
            message.success('已复制');
        } catch (e) {
            message.error('复制失败，请重试');
        }
    };

    const handleFetchRealPreviewJson = async () => {
        try {
            await fetchPodClientConfig();
            message.success('刷新成功');
        } catch {
            message.error('刷新失败，请稍后重试');
        }
    };

    if (loadingPodConfig && commonConfigData === undefined) {
        return <div className="bg-white rounded-lg shadow-sm p-4">加载配置中…</div>;
    }

    if (!currentConfig || !localeData) {
        return <div className="bg-white rounded-lg shadow-sm p-4">未找到可编辑配置</div>;
    }

    return (
        <div className="max-w-full overflow-x-hidden bg-white rounded-lg shadow-sm px-4 pb-0 pt-2">
            {showMissingServerKeyHint ? (
                <div
                    className="p-3 rounded mb-2"
                    style={{
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7',
                        color: '#ff4d4f'
                    }}
                >
                    <div className="text-sm font-bold">
                        <strong>提示：</strong>
                        <span>
                            后端暂无该配置项，保存后将创建。
                        </span>
                    </div>
                </div>
            ) : null}


            <input
                ref={exampleFileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                className="sr-only"
                tabIndex={-1}
                onChange={onExampleFileSelected}
            />

            <div
                ref={fourColumnLayoutRef}
                className="grid w-full min-w-0 grid-cols-1 gap-0 lg:grid-cols-[280px_1fr_1fr_1fr] lg:items-stretch"
            >
                <div className="flex min-h-0 min-w-0 flex-col rounded-lg bg-white shadow-sm lg:mr-2">
                    <div
                        className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain py-3 px-0"
                        style={{ maxHeight: leftEditorScrollMaxPx }}
                    >
                        <div className="px-3 pb-2">
                            <Tabs
                                activeKey={activePodMode}
                                onChange={(k) => setActivePodMode(k as PodModeKey)}
                                size="small"
                                items={[
                                    { key: 'cameraDoorNumber', label: '普通POD', children: <div className="hidden" aria-hidden /> },
                                    { key: 'cameraDoorSubNumber', label: '特殊POD', children: <div className="hidden" aria-hidden /> }
                                ]}
                                className="mb-2 [&_.ant-tabs-content-holder]:hidden [&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-tab]:font-bold"
                            />
                        </div>
                        <Title level={5} className="!mb-3 !mt-0 pb-2">
                            预览
                        </Title>
                        <Tabs
                            activeKey={previewLocale}
                            centered
                            onChange={(key) => setPreviewLocale(key)}
                            size="small"
                            items={SUPPORTED_LOCALES.map((lm) => ({
                                key: lm.key,
                                label: lm.label,
                                children: <div className="hidden" aria-hidden />
                            }))}
                            className="mb-2 [&_.ant-tabs-content-holder]:hidden [&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-tab]:font-bold"
                        />

                        <div className="flex justify-center">
                            <div className="w-full max-w-[280px] rounded-[24px] border-[0px] border-black bg-black p-1.5 shadow-lg">
                                <div className="relative h-[480px] w-full overflow-hidden rounded-[18px] bg-[#111]">
                                    <div className="absolute left-0 right-0 top-0 z-20 flex h-10 items-center justify-between bg-black px-1.5 text-white">
                                        <Tooltip title={mobilePreviewStepIndex > 0 ? '上一张' : ''}>
                                            <button
                                                type="button"
                                                className="min-h-[26px] min-w-[2rem] cursor-pointer px-1 py-0.5 text-center text-[10px] font-medium text-white outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/55 focus-visible:ring-offset-0 hover:bg-white/10 active:scale-[0.97] disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-white/35"
                                                onClick={() =>
                                                    setMobilePreviewStepIndex((prev) => Math.max(0, prev - 1))
                                                }
                                                disabled={mobilePreviewStepIndex <= 0}
                                            >
                                                <LeftOutlined />
                                            </button>
                                        </Tooltip>
                                        <div className="flex-1 min-w-0 truncate px-2 text-center text-[11px] font-semibold leading-tight transition-opacity duration-300">
                                            {mobilePreviewStep?.title || '预览标题'}
                                        </div>
                                        <Tooltip title={mobilePreviewStepIndex < localeData.steps.length - 1 ? '下一张' : ''}>
                                            <button
                                                type="button"
                                                className="min-h-[26px] min-w-[2rem] cursor-pointer px-1 py-0.5 text-center text-[10px] font-medium text-white outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/55 focus-visible:ring-offset-0 hover:bg-white/10 active:scale-[0.97] disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-white/35"
                                                onClick={() =>
                                                    setMobilePreviewStepIndex((prev) =>
                                                        Math.min(localeData.steps.length - 1, prev + 1)
                                                    )
                                                }
                                                disabled={mobilePreviewStepIndex >= localeData.steps.length - 1}
                                            >
                                                <RightOutlined />
                                            </button>
                                        </Tooltip>
                                    </div>

                                    <div className="absolute left-0 right-0 top-10 z-20 flex h-7 items-center bg-[#d9a7a7] px-2 text-[10px] text-white transition-opacity duration-300">
                                        {localeData.persistentNotice}
                                    </div>

                                    <div className="absolute inset-0 bg-gradient-to-br from-[#5a6e7f] via-[#294d61] to-[#162430]" />

                                    <div className="absolute right-2 top-[7.5rem] z-20 transition-all duration-300 ease-in-out">
                                        {examplePreviewSrc ? (
                                            <div className="h-[72px] w-[54px] overflow-hidden rounded border border-white/70 bg-black/40 transition-transform duration-300 hover:scale-105">
                                                <img
                                                    src={examplePreviewSrc}
                                                    alt=""
                                                    className="h-full w-full object-cover transition-opacity duration-300"
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex h-[72px] w-[54px] items-start justify-center rounded border border-white/70 bg-white/20 pt-1 text-[10px] text-white backdrop-blur transition-transform duration-300 hover:scale-105">
                                                {previewLocale === 'en'
                                                    ? 'Examples'
                                                    : previewLocale === 'es'
                                                      ? 'Ejemplos'
                                                      : '拍摄示例'}
                                            </div>
                                        )}
                                    </div>

                                    <div className="absolute bottom-16 left-2 right-2 z-20 space-y-1.5">
                                        <div className="rounded-md bg-black/55 px-2 py-1.5 text-[13px] font-semibold text-white transition-all duration-300 transform hover:translate-y-[-2px]">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: resolvePreviewRichHtml(
                                                        mobilePreviewStep?.differentialTipRichText,
                                                        activePodMode,
                                                        previewLocale,
                                                        mobilePreviewStep?.key ?? '',
                                                        'differential'
                                                    )
                                                }}
                                            />
                                        </div>
                                        <div className="rounded-md bg-black/55 px-2 py-1.5 text-[12px] leading-5 text-white transition-all duration-300 transform hover:translate-y-[-2px]">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: resolvePreviewRichHtml(
                                                        mobilePreviewStep?.requirementRichText,
                                                        activePodMode,
                                                        previewLocale,
                                                        mobilePreviewStep?.key ?? '',
                                                        'requirement'
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="absolute bottom-0 left-0 right-0 z-20 flex h-14 items-center justify-center bg-black">
                                        <div className="h-12 w-12 rounded-full border-[3px] border-white bg-[#f2f2f2] transition-transform duration-300 hover:scale-105" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 border-t border-gray-100 pt-3">
                            <div className="mb-1.5 flex items-center gap-2">
                                <Text strong>返回 JSON（客户端同款）</Text>
                                <Tooltip title="刷新">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<ReloadOutlined />}
                                        loading={loadingPodClientConfig}
                                        className="!inline-flex !h-7 !items-center !px-1 !py-0 !text-[#1677ff] hover:!text-[#4096ff]"
                                        onClick={handleFetchRealPreviewJson}
                                    />
                                </Tooltip>
                                <Tooltip title="复制">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        className="!inline-flex !h-7 !items-center !px-1 !py-0 !text-[#1677ff] hover:!text-[#4096ff]"
                                        onClick={() => handleCopySavedJson()}
                                    />
                                </Tooltip>
                            </div>
                            <pre className="max-h-[min(55vh,560px)] min-h-[10rem] overflow-auto rounded bg-gray-50 p-2 text-[11px] leading-relaxed">
                                {JSON.stringify(podClientData?.getPodCaptureConfigForClient ?? currentModeClientJson ?? {}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>

                {SUPPORTED_LOCALES.map((localeMeta) => {
                    const localeCol = getLocaleContent(currentConfig, localeMeta.key, activePodMode);
                    return (
                        <div
                            key={localeMeta.key}
                            className="flex min-h-0 min-w-0 flex-col rounded-lg bg-white shadow-sm"
                        >
                            <div
                                ref={(el) => {
                                    localeColumnScrollRefs.current[localeMeta.key] = el;
                                }}
                                className={`min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain py-3 px-0 ${
                                    localeMeta.key === 'zh' || localeMeta.key === 'en' ? 'pr-1' : ''
                                }`}
                                style={{ maxHeight: leftEditorScrollMaxPx }}
                                onScroll={(ev) => onLocaleColumnScroll(ev, localeMeta.key)}
                            >
                                <Title level={5} className="!mb-3 !mt-0 pb-2">
                                    {localeMeta.label}
                                </Title>

                                <Text strong className="mb-1 block">
                                    通用提示语
                                </Text>
                                <Input
                                    value={localeCol.persistentNotice}
                                    onFocus={() => setPreviewLocale(localeMeta.key)}
                                    onChange={(e) =>
                                        updateLocaleData(localeMeta.key, (prev) => ({
                                            ...prev,
                                            persistentNotice: e.target.value
                                        }))
                                    }
                                />

                                <Text strong className="mb-2 mt-4 block">
                                    拍摄步骤
                                </Text>
                                <div className="flex flex-col gap-2.5">
                                    {localeCol.steps.map((step, idx) => {
                                        const stKey = examplePreviewStorageKey(idx, activePodMode);
                                        const localeStep = localeCol.steps[idx];
                                        const localSrc = localExamplePreviewByKey[stKey];
                                        const serverUrl = localeStep?.exampleImageUrl ?? null;
                                        const resolvedUrlFromClient = getResolvedExampleUrlFromClient(idx, localeMeta.key);
                                        const previewableServerUrl = resolveExamplePreviewUrl(serverUrl);
                                        const showImg =
                                            localSrc ||
                                            resolvedUrlFromClient ||
                                            previewableServerUrl;
                                        const hasUploadedExample =
                                            Boolean(localSrc) || Boolean(serverUrl?.trim());
                                        return (
                                            <div
                                                key={`${localeMeta.key}-${step.stepNo}-${idx}`}
                                                className={`rounded-md border p-3 transition-colors ${
                                                    mobilePreviewStepIndex === idx
                                                        ? 'border-blue-300 bg-blue-50/40'
                                                        : 'border-gray-100'
                                                }`}
                                                ref={(el) => {
                                                    if (localeMeta.key === BASE_LOCALE) {
                                                        stepCardRefs.current[idx] = el;
                                                    }
                                                }}
                                                data-step-index={idx}
                                                onClick={() => focusStepForPreview(idx, 0)}
                                                onFocusCapture={() => focusStepForPreview(idx, 0)}
                                            >
                                                <Text strong className="mb-2 block">
                                                    {toCnStepLabel(step.stepNo)}
                                                </Text>
                                                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Text type="secondary" className="!text-xs">
                                                            {localeMeta.key === 'en'
                                                                ? 'Examples'
                                                                : localeMeta.key === 'es'
                                                                  ? 'Ejemplos'
                                                                  : '拍摄示例'}
                                                        </Text>
                                                        <div className="mt-2">
                                                            <Button
                                                                type="default"
                                                                size="small"
                                                                loading={uploadingExampleKey === stKey}
                                                                onClick={() => {
                                                                    focusStepForPreview(idx, 40);
                                                                    setPreviewLocale(localeMeta.key);
                                                                    openExampleFilePicker(idx);
                                                                }}
                                                            >
                                                                {hasUploadedExample ? '重新上传' : '上传示例'}
                                                            </Button>
                                                            {showImg ? (
                                                                <div className="mt-2 max-w-full overflow-hidden rounded border border-gray-200 bg-gray-50">
                                                                    <img
                                                                        src={showImg}
                                                                        alt=""
                                                                        className="h-20 w-full max-w-[200px] object-contain bg-black/5"
                                                                    />
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <Text type="secondary" className="!text-xs">
                                                            标题
                                                        </Text>
                                                        <Input
                                                            value={localeStep?.title || ''}
                                                            onFocus={() => {
                                                                focusStepForPreview(idx, 70);
                                                                setPreviewLocale(localeMeta.key);
                                                            }}
                                                            onChange={(e) =>
                                                                updateLocaleData(localeMeta.key, (locale) => {
                                                                    const steps = locale.steps.map((s, sIdx) =>
                                                                        sIdx === idx
                                                                            ? { ...s, title: e.target.value }
                                                                            : s
                                                                    );
                                                                    return { ...locale, steps };
                                                                })
                                                            }
                                                        />
                                                    </div>

                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Text type="secondary" className="!text-xs">
                                                            本步是否可跳过
                                                        </Text>
                                                        <Radio.Group
                                                            className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1"
                                                            value={resolveStepSkippable(localeStep)}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                const next =
                                                                    raw === true ||
                                                                    raw === 'true' ||
                                                                    raw === 1 ||
                                                                    raw === '1';
                                                                setStepSkippableAllLocales(idx, next);
                                                                focusStepForPreview(idx, 0);
                                                                setPreviewLocale(localeMeta.key);
                                                            }}
                                                        >
                                                            <Radio value={false}>不可跳过</Radio>
                                                            <Radio value={true}>可跳过</Radio>
                                                        </Radio.Group>
                                                    </div>

                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <div className="mb-0.5 flex flex-wrap items-center gap-x-1 gap-y-0">
                                                            <Text type="secondary" className="!text-xs">
                                                                差异化提示语
                                                            </Text>
                                                            <Tooltip title="恢复为系统内置默认文案">
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    className="!h-auto shrink-0 !p-0 !text-xs !leading-none"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        restoreStepRichTextField(
                                                                            localeMeta.key,
                                                                            idx,
                                                                            localeStep?.key ?? '',
                                                                            'differentialTipRichText'
                                                                        );
                                                                    }}
                                                                >
                                                                    恢复默认
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                        <RichTextColorEditor
                                                            value={localeStep?.differentialTipRichText || ''}
                                                            onChange={(html) =>
                                                                updateLocaleData(localeMeta.key, (locale) => {
                                                                    const steps = locale.steps.map((s, sIdx) =>
                                                                        sIdx === idx
                                                                            ? {
                                                                                  ...s,
                                                                                  differentialTipRichText: html
                                                                              }
                                                                            : s
                                                                    );
                                                                    return { ...locale, steps };
                                                                })
                                                            }
                                                            placeholder="请输入差异化提示语"
                                                            onFocus={() => {
                                                                focusStepForPreview(idx, 180);
                                                                setPreviewLocale(localeMeta.key);
                                                            }}
                                                        />
                                                    </div>

                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <div className="mb-0.5 flex flex-wrap items-center gap-x-1 gap-y-0">
                                                            <Text type="secondary" className="!text-xs">
                                                                拍摄要求
                                                            </Text>
                                                            <Tooltip title="恢复为系统内置默认文案">
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    className="!h-auto shrink-0 !p-0 !text-xs !leading-none"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        restoreStepRichTextField(
                                                                            localeMeta.key,
                                                                            idx,
                                                                            localeStep?.key ?? '',
                                                                            'requirementRichText'
                                                                        );
                                                                    }}
                                                                >
                                                                    恢复默认
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                        <RichTextColorEditor
                                                            value={localeStep?.requirementRichText || ''}
                                                            onChange={(html) =>
                                                                updateLocaleData(localeMeta.key, (locale) => {
                                                                    const steps = locale.steps.map((s, sIdx) =>
                                                                        sIdx === idx
                                                                            ? { ...s, requirementRichText: html }
                                                                            : s
                                                                    );
                                                                    return { ...locale, steps };
                                                                })
                                                            }
                                                            placeholder="请输入拍摄要求"
                                                            onFocus={() => {
                                                                focusStepForPreview(idx, 360);
                                                                setPreviewLocale(localeMeta.key);
                                                            }}
                                                        />
                                                    </div>
                                                </Space>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}

            <div className="sticky bottom-0 z-20 mt-2 flex justify-end gap-4 bg-white py-1 lg:col-start-2 lg:col-span-3">
                <Button
                    type="primary"
                    onClick={addStep}
                    className="w-auto min-w-24 shrink-0"
                >
                    <span className="font-bold">添加张数</span>
                </Button>
                <Button
                    type="default"
                    danger
                    disabled={localeData.steps.length <= 1}
                    onClick={() => removeStep(localeData.steps.length - 1)}
                    className="w-auto min-w-24 shrink-0"
                >
                    <span className="font-bold">删除最后一张</span>
                </Button>
                <Button
                    type="primary"
                    onClick={handleSave}
                    loading={savingPodConfig}
                    className="min-w-24 font-bold"
                >
                    保存
                </Button>
            </div>
            </div>
        </div>
    );
};

export default PodCaptureConfigPage;